/** @file Creates a bone under a parent (local setup transform; the last sibling by default). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import type { LocalTransform } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { defineCreate } from '../_shared/defineCreate';
import { assertUserParent } from './assertUserParent';

const create = defineCreate({
  domain: 'bone',
  kind: 'bone',
  description: 'Create a bone under `parent` (name or id; not a generated socket bone) with an optional local setup transform, length and inherit mode.',
  schema: z
    .object({
      id: z.string().exactOptional(),
      name: z.string().min(1),
      parent: z.string().min(1),
      length: prim.finite.nonnegative().exactOptional(),
      inherit: prim.inherit.exactOptional(),
      setup: prim.localTransform.partial().exactOptional(),
    })
    .strict(),
  build(input, ctx) {
    const parent = ctx.resolve('bone', input.parent);
    const setup: LocalTransform = { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, shearX: 0, shearY: 0 };
    Object.assign(setup, input.setup);
    // siblingIndex is placed by `apply` below, which can read the siblings' indices from the draft.
    return { name: input.name, parent, siblingIndex: 0, length: input.length ?? 0, inherit: input.inherit ?? 'normal', setup };
  },
});

/**
 * bone.create: the new bone sorts last — the last sibling's siblingIndex + 1 (0 for the first child), gaps kept; a
 * generated socket bone is refused as parent exactly as bone.setParent refuses it (final review m17).
 */
export const command = defineCommand({
  ...create,
  apply(draft, input, ctx) {
    assertUserParent(draft, ctx.resolve('bone', input.parent));
    create.apply(draft, input, ctx);
    const bone = draft.bones[ctx.created[input.name] ?? ''];
    if (!bone?.parent) return;
    const last = ctx.index.children.get(bone.parent)?.at(-1);
    bone.siblingIndex = last === undefined ? 0 : (draft.bones[last]?.siblingIndex ?? -1) + 1;
  },
});
